import { createSelector } from "reselect";
import AppState from "../AppState";
import { HeatMapDateRangeState, HeatMapState } from "./HeatMapTypes";
import {
  ColorScale,
  computeColorScales,
  computeHeatMapGradient,
  normalizeAnalyticsDate,
  resolveDateRange,
  ResolvedDateRange,
} from "../../utils/HeatMapUtils";
import {
  selectCurrentPage,
  selectContentSlots,
  selectProductListSequenceMode,
  selectProductListsFetched,
  selectProductListEditSequence,
} from "../product-list/ProductListSelectors";
import {
  selectAllProductsFetched,
  selectAnalyticsStartDate,
  selectAnalyticsEndDate,
  selectIsFetchedBySearch,
  selectProductListBySearch,
} from "../product/ProductSelectors";
import {
  selectAccountFeatures,
  selectCallerAccountId,
} from "../app-config/AppConfigSelectors";
import { AnalyticsData, Product } from "../product/ProductTypes";
import { getCurrentPageProductIds } from "../../utils/ProductUtil";
import { isHeatMapMetricDateReliant } from "../../components/heat-map-modal/HeatMapMetricSelect";

/**
 * Number of catalog pages per heat map batch. With the default page size
 * of 24, a batch holds up to 240 products. The exact page size is
 * resolved per-page by `getCurrentPageProductIds`.
 */
const PAGES_PER_BATCH = 10;

export const selectHeatMapState = (state: AppState): HeatMapState =>
  state.heatmap;

export const selectHeatMapMetric = (state: AppState): string =>
  state.heatmap.metric;

export const selectHeatMapDateRange = (
  state: AppState,
): HeatMapDateRangeState => state.heatmap.dateRange;

export const selectHeatMapHighColor = (state: AppState): string =>
  state.heatmap.highColor;

export const selectHeatMapLowColor = (state: AppState): string =>
  state.heatmap.lowColor;

export const selectHeatMapIsFetching = (state: AppState): boolean =>
  state.heatmap.pendingRequests > 0;

export const selectHeatMapLastFetchedStartDate = (
  state: AppState,
): string | undefined => state.heatmap.lastFetchedStartDate;

export const selectHeatMapLastFetchedEndDate = (
  state: AppState,
): string | undefined => state.heatmap.lastFetchedEndDate;

export const selectHeatMapLastSettledRangeKey = (state: AppState): string =>
  state.heatmap.lastSettledRangeKey;

export const selectHeatMapFreshProductCodes = (
  state: AppState,
): Record<string, true> => state.heatmap.freshProductCodes;

export const selectIsHeatMapGradientOnGridFeatureEnabled = (
  state: AppState,
): boolean => {
  const accountId = selectCallerAccountId(state);
  const features = selectAccountFeatures(state, accountId);
  return Boolean(features?.["heat-map-gradient-on-grid"]);
};

export const selectDisplayGradientOnGrid = createSelector(
  [
    (state: AppState) => state.heatmap.displayGradientOnGrid,
    selectIsHeatMapGradientOnGridFeatureEnabled,
  ],
  (flag, featureEnabled): boolean => flag && featureEnabled,
);

const selectProductAnalyticsCacheRaw = (
  state: AppState,
): Record<string, AnalyticsData[]> => state.products.analytics.productAnalytics;

export const getHeatMapMetricValueForProduct = (
  metric: string,
  product: Product | undefined,
  productAnalyticsCache: Record<string, AnalyticsData[]>,
): number | undefined => {
  if (!product) return undefined;
  switch (metric) {
    case "Inventory":
      return product.stock;
    case "Stock Value":
      return product.stockValue;
    case "SKU Coverage":
      return product.skuCoverage;
    case "Price":
      return product.lowPrice ?? product.highPrice;
    default: {
      const analyticsId = product.analyticsId;
      if (!analyticsId) return undefined;
      const analytics = productAnalyticsCache[analyticsId];
      if (!analytics) return undefined;
      const found = analytics.find((a) => a.name === metric);
      const raw = found?.value;
      const num =
        typeof raw === "number"
          ? raw
          : typeof raw === "string"
            ? parseFloat(raw)
            : NaN;
      return Number.isFinite(num) ? num : undefined;
    }
  }
};


/**
 * Memoized: 8-color gradient computed from the high/low color choice.
 * Recomputes only when high or low color changes.
 */
export const selectHeatMapGradient = createSelector(
  [selectHeatMapHighColor, selectHeatMapLowColor],
  (high, low) => computeHeatMapGradient(high, low),
);

export const selectHeatMapResolvedDateRange = createSelector(
  [selectHeatMapDateRange],
  (dateRange): ResolvedDateRange =>
    resolveDateRange(
      dateRange.option,
      dateRange.customStartDate,
      dateRange.customEndDate,
    ),
);

/**
 * Memoized: product IDs displayed on the **current** product list page,
 * matching the same pagination + content-slot logic as the main grid.
 *
 * The heat map planogram is scoped to the current page only — not the entire
 * category — so the visualization aligns with what the merchandiser is
 * currently viewing.
 */
export interface HeatMapBatchPage {
  pageNumber: number;
  productIds: string[];
}

export interface HeatMapBatchPages {
  pages: HeatMapBatchPage[];
  totalProductCount: number;
}

/**
 * Memoized: the up-to-10 catalog pages that make up the current heat map
 * batch (≤240 products). The active batch is determined by the catalog
 * page the user is on at modal open: pages 1–10 → batch 0, 11–20 → batch
 * 1, etc.
 *
 * Each entry's `pageNumber` is the actual catalog page number (1-indexed)
 * and is used for the `P#N` label in the planogram.
 *
 * Pages whose product list comes back empty (end-of-catalog) are dropped,
 * so a batch may have fewer than 10 entries near the end.
 */
export const selectHeatMapBatchPages = createSelector(
  [
    selectContentSlots,
    selectCurrentPage,
    selectIsFetchedBySearch,
    selectProductListBySearch,
    selectProductListSequenceMode,
    selectProductListsFetched,
    selectProductListEditSequence,
  ],
  (
    contentSlots,
    currentPage,
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ): HeatMapBatchPages => {
    const batchIndex = Math.max(
      0,
      Math.floor((currentPage - 1) / PAGES_PER_BATCH),
    );
    const pages: HeatMapBatchPage[] = [];
    let totalProductCount = 0;

    for (let i = 0; i < PAGES_PER_BATCH; i++) {
      const pageNumber = batchIndex * PAGES_PER_BATCH + i + 1;
      const productIds = getCurrentPageProductIds(
        contentSlots,
        pageNumber,
        isFetchedBySearch,
        productsBySearch,
        sequenceMode,
        products,
        editProducts,
      ) as string[];
      if (productIds && productIds.length > 0) {
        pages.push({ pageNumber, productIds });
        totalProductCount += productIds.length;
      }
    }

    return { pages, totalProductCount };
  },
);

/** @deprecated kept for backward compatibility — use `selectHeatMapBatchPages`. */
export const selectHeatMapCurrentPageProductIds = createSelector(
  [
    selectContentSlots,
    selectCurrentPage,
    selectIsFetchedBySearch,
    selectProductListBySearch,
    selectProductListSequenceMode,
    selectProductListsFetched,
    selectProductListEditSequence,
  ],
  (
    contentSlots,
    currentPage,
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ): string[] => {
    return getCurrentPageProductIds(
      contentSlots,
      currentPage,
      isFetchedBySearch,
      productsBySearch,
      sequenceMode,
      products,
      editProducts,
    ) as string[];
  },
);

export const selectHeatMapCacheIsFreshForRange = createSelector(
  [
    selectAnalyticsStartDate,
    selectAnalyticsEndDate,
    selectHeatMapResolvedDateRange,
  ],
  (cachedStartDate, cachedEndDate, resolvedDateRange): boolean =>
    normalizeAnalyticsDate(resolvedDateRange.startDate) ===
      normalizeAnalyticsDate(cachedStartDate) &&
    normalizeAnalyticsDate(resolvedDateRange.endDate) ===
      normalizeAnalyticsDate(cachedEndDate),
);

export const selectHeatMapColorScales = createSelector(
  [
    selectHeatMapMetric,
    selectHeatMapGradient,
    selectHeatMapBatchPages,
    selectHeatMapFreshProductCodes,
    selectAllProductsFetched,
    selectProductAnalyticsCacheRaw,
    selectHeatMapCacheIsFreshForRange,
  ],
  (
    metric,
    gradient,
    batchPages,
    freshProductCodes,
    productsById,
    productAnalyticsCache,
    cacheIsFreshForRange,
  ): ColorScale[] => {
    const isDateReliant = isHeatMapMetricDateReliant(metric);
    const values: number[] = [];
    batchPages.pages.forEach((page) => {
      page.productIds.forEach((productId) => {
        const product = productsById[productId];
        const analyticsId = product?.analyticsId;
        const isFresh = !isDateReliant
          ? true
          : Boolean(
              (analyticsId && freshProductCodes[analyticsId]) ||
                (cacheIsFreshForRange &&
                  analyticsId &&
                  productAnalyticsCache[analyticsId]),
            );
        if (!isFresh) return;
        const value = getHeatMapMetricValueForProduct(
          metric,
          product,
          productAnalyticsCache,
        );
        if (value !== undefined) values.push(value);
      });
    });
    return computeColorScales(values, gradient);
  },
);

/**
 * Re-export the analytics cache selector for hooks that need to
 * resolve per-product values (e.g. the grid-tile color hook).
 */
export const selectProductAnalyticsCache = selectProductAnalyticsCacheRaw;
